// src/sitemap.ts
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { getCookie } from 'app/shared/utils/cookie.utils';
import { UilCube } from '@iconscout/react-unicons';
import { getFirstDimensionId } from 'helpers/utils';

/** ---------------- Existing route types ---------------- */
export interface Route {
  name: string;
  icon?: IconProp | string | string[];
  iconSet?: 'font-awesome' | 'feather' | 'unicons';
  pages?: Route[];
  path?: string;
  pathName?: string;
  flat?: boolean;
  topNavIcon?: string;
  dropdownInside?: boolean;
  active?: boolean;
  new?: boolean;
  hasNew?: boolean;
  next?: boolean;
  /** Subtitle/description for mega menu item */
  description?: string;
  /** Title for the detail view in complex mega menus */
  detailTitle?: string;
  /** Description for the detail view in complex mega menus */
  detailDescription?: string;
  /** Action link text for detail view */
  detailActionLabel?: string;
}

export interface MegaMenuSection {
  sectionTitle: string;
  icon?: string;
  items: Route[];
  allLink?: { label: string; path: string };
  containerClass?: string;
  titleClass?: string;
}

export interface MegaMenuBottomBar {
  icon?: string;
  label: string;
  linkText: string;
  linkPath: string;
}

export interface MegaMenuPromoCard {
  title: string;
  description: string;
}

export interface RouteItems {
  label: string;
  horizontalNavLabel?: string;
  icon: typeof UilCube;
  labelDisabled?: boolean;
  pages: Route[];
  /** Direct link path (when no submenu) */
  path?: string;
  megaMenu?: boolean;
  /** Optional: explicit sections for mega menu (FEATURES, PREMIUM, SOLUTIONS style) */
  megaMenuSections?: MegaMenuSection[];
  active?: boolean;
  megaMenuBottomBar?: MegaMenuBottomBar;
  megaMenuLayout?: 'default' | 'dashboard' | 'apps' | 'modern';
  /** Sections rendered in a second row (e.g. Divisions) */
  megaMenuSecondRowSections?: MegaMenuSection[];
  /** Promo card shown in first row (e.g. gradient container with text) */
  megaMenuPromoCard?: MegaMenuPromoCard;
}

const INVALID_PROJECT_IDS = new Set(['', 'null', 'undefined', 'ALL']);

/** Returns a usable projectId from localStorage, or empty when none is selected. */
export const getStoredProjectId = (): string => {
  const raw = localStorage.getItem('projectId')?.trim() ?? '';
  if (INVALID_PROJECT_IDS.has(raw)) return '';
  return raw;
};

/** ---------------- Build sitemap ---------------- */
export const getRoutes = (): RouteItems[] => {
  const userId = localStorage.getItem('userId') || '';
  const userRole = (localStorage.getItem('role') || '').toLowerCase();

  const activeProduct = getCookie('activeProduct');
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const isCSAPlus = activeProduct === 'CSA+' || pathname.startsWith('/csa+');
  const isMTCPlus = activeProduct === 'MTC+' || pathname.startsWith('/mtc+');
  const isATEPlus = activeProduct === 'ATE+' || pathname.startsWith('/ate+');
  const isTSCPlus = activeProduct === 'TSC+' || pathname.startsWith('/project');
  const hideUserManagementNav = isCSAPlus || isMTCPlus || isTSCPlus || isATEPlus;
  const hideDivisionsNav = isCSAPlus || isMTCPlus || isTSCPlus || isATEPlus;
  const hideReviewerAuditNav = isCSAPlus || isMTCPlus || isATEPlus;
  const allProjectsPath = isCSAPlus
    ? '/csa+/all-projects'
    : isMTCPlus
    ? '/mtc+/all-projects'
    : isATEPlus
    ? '/ate+/all-projects'
    : `/project/${userId}/all-projects`;
  const createProjectPath = isCSAPlus
    ? '/csa+/create-project'
    : isMTCPlus
    ? '/mtc+/create-project'
    : isATEPlus
    ? '/ate+/create-project'
    : `/project/${userId}/create-project`;
  const projectManagementPages: Route[] = [
    {
      name: 'All Projects',
      path: allProjectsPath,
      pathName: 'project-test-project-list-view',
      active: true
    }
  ];

  if (userRole !== 'developer' && userId) {
    projectManagementPages.unshift({
      name: 'Create New',
      path: createProjectPath,
      pathName: 'project-management-create-new',
      active: true
    });
  }

  const pages: Route[] = [
    {
      name: 'project-management',
      icon: 'clipboard',
      active: true,
      pages: projectManagementPages
    },
    {
      name: 'ethico',
      icon: 'clipboard',
      active: true,
      pages: [
        {
          name: 'ethico Gov360',
          path: `/ethico/ethico-lifesciences`,
          pathName: 'ethico-lifesciences',
          active: true
        },
        {
          name: 'ethico MedGuard',
          path: `/ethico`,
          pathName: 'chat',
          active: true
        }
      ]
    }
  ];

  // User Management and Divisions in Admin nav (product-specific visibility)
  if (userRole !== 'developer') {
    if (!hideUserManagementNav) {
      pages.splice(1, 0, {
        name: 'user-management',
        icon: 'users',
        active: true,
        pages: [
          {
            name: 'Add User',
            path: `/users/add`,
            pathName: 'users-add',
            active: true
          },
          {
            name: 'View All Users',
            path: `/users/view-all`,
            pathName: 'users-view-all',
            active: true
          }
        ]
      });
    }

    if (!hideDivisionsNav) {
      const divisionsInsertIndex = hideUserManagementNav ? 1 : 2;
      pages.splice(divisionsInsertIndex, 0, {
        name: 'divisions',
        icon: 'layers',
        active: true,
        pages: [
          {
            name: 'View Divisions',
            path: `/divisions/view-all`,
            pathName: 'divisions-view-all',
            active: true
          },
          {
            name: 'Create Division',
            path: `/divisions/create`,
            pathName: 'divisions-create',
            active: true
          }
        ]
      });
    }
  }

  // Apps mega menu: col-8 = 3 menus horizontally (Project Management, Ethico, User Management) + Divisions in second row; col-4 = gradient promo
  const projectManagementItemDescriptions: Record<string, string> = {
    'Create New': 'Start a new project.',
    'All Projects': 'View and manage all your projects.'
  };
  const appsMegaMenuFirstRow: MegaMenuSection[] = [
    {
      sectionTitle: 'Project',
      icon: 'folder',
      items: projectManagementPages.map(p => {
        const desc = projectManagementItemDescriptions[p.name] ?? '';
        return { ...p, icon: 'folder', description: desc };
      })
    },
    {
      sectionTitle: 'Ethico',
      icon: 'clipboard',
      items: [
        {
          name: 'ethico Gov360',
          path: `/ethico/ethico-lifesciences`,
          pathName: 'ethico-lifesciences',
          active: true,
          icon: 'clipboard',
          description: 'Governance and compliance for life sciences.'
        },
        {
          name: 'ethico MedGuard',
          path: `/ethico`,
          pathName: 'chat',
          active: true,
          icon: 'clipboard',
          description: 'AI-powered medical guardrails and chat.'
        }
      ]
    },
    ...(userRole !== 'developer' && !hideUserManagementNav
      ? [
          {
            sectionTitle: 'User Management',
            icon: 'users',
            items: [
              {
                name: 'Add User',
                path: `/users/add`,
                pathName: 'users-add',
                active: true,
                icon: 'user-plus',
                description: 'Invite new users to the platform.'
              },
              {
                name: 'View All Users',
                path: `/users/view-all`,
                pathName: 'users-view-all',
                active: true,
                icon: 'users',
                description: 'Manage roles and permissions.'
              }
            ]
          } satisfies MegaMenuSection
        ]
      : [])
  ];

  const appsMegaMenuSecondRow: MegaMenuSection[] =
    userRole !== 'developer' && !hideDivisionsNav
      ? [
          {
            sectionTitle: 'Divisions',
            icon: 'layers',
            items: [
              {
                name: 'View Divisions',
                path: `/divisions/view-all`,
                pathName: 'divisions-view-all',
                active: true,
                icon: 'layers',
                description: 'Browse and manage divisions.'
              },
              {
                name: 'Create Division',
                path: `/divisions/create`,
                pathName: 'divisions-create',
                active: true,
                icon: 'plus-circle',
                description: 'Create a new division.'
              }
            ]
          }
        ]
      : [];

  const appsMegaMenuPromoCard: MegaMenuPromoCard = {
    title: 'Platform overview',
    description:
      hideUserManagementNav && hideDivisionsNav
        ? 'Manage projects and governance from one place. Get started with the links in this menu.'
        : 'Manage projects, governance, users and divisions from one place. Get started with the links in this menu.'
  };

  const basePath = isCSAPlus ? '/csa+' : isMTCPlus ? '/mtc+' : isATEPlus ? '/ate+' : '/project';
  const storedProjectId = getStoredProjectId();
  const defaultProjectPath = (() => {
    if (isCSAPlus) {
      return storedProjectId ? `/csa+/project-details/${encodeURIComponent(storedProjectId)}` : '/csa+/all-projects';
    }
    if (isMTCPlus) {
      return storedProjectId ? `/mtc+/project-details/${encodeURIComponent(storedProjectId)}` : '/mtc+/all-projects';
    }
    if (isATEPlus) {
      return storedProjectId ? `/ate+/project/${encodeURIComponent(storedProjectId)}` : '/ate+/all-projects';
    }
    if (storedProjectId) {
      return `/project/details-tabs?projectId=${encodeURIComponent(storedProjectId)}&nav=summary`;
    }
    return `/project/${userId}/all-projects`;
  })();
  const reviewerAuditPath = isMTCPlus
    ? '/mtc+/all-projects'
    : isATEPlus
    ? '/ate+/all-projects'
    : `${basePath}/details-tabs?dimension-id=${getFirstDimensionId()}&nav=whitelist&whitelistTab=2`;

  const routes: RouteItems[] = [
    {
      label: 'Project',
      horizontalNavLabel: 'Project',
      icon: UilCube,
      pages: [],
      path: defaultProjectPath
    },
    ...(hideReviewerAuditNav
      ? []
      : [
          {
            label: 'Reviewer / Audit',
            horizontalNavLabel: 'Reviewer / Audit',
            icon: UilCube,
            pages: [],
            path: reviewerAuditPath
          } satisfies RouteItems
        ]),
    {
      label: 'AI Assurance 360',
      horizontalNavLabel: 'AI Assurance 360',
      icon: UilCube,
      pages: [],
      path: `${basePath}/ai-assurance`
    },
    {
      label: 'Realtime Monitoring',
      horizontalNavLabel: 'Realtime Monitoring',
      icon: UilCube,
      pages: [],
      path: `${basePath}/realtime-monitoring`
    },
    {
      label: 'Admin',
      horizontalNavLabel: 'Admin',
      icon: UilCube,
      pages,
      megaMenuLayout: 'modern',
      megaMenuSections: appsMegaMenuFirstRow,
      megaMenuSecondRowSections: appsMegaMenuSecondRow,
      megaMenuPromoCard: appsMegaMenuPromoCard
    }
  ];

  return routes;
};
